// src/pages/planning/usePlanning.ts — shared planning state for the Snapshot /
// Matrix / Calendar tabs + the day-detail drawer (the bp-app.jsx planning store,
// lifted to React state). Loads /api/days once; holds the dept filter + the
// open day; ack/addNote update optimistically then persist (notifications for
// @mentions are simulated as a toast, per the prototype).

import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { getDays, postNote, ackNote, editNote as apiEditNote, deleteNote as apiDeleteNote, type PlanDay, type NoteInput } from "../../lib/planApi";
import { PEOPLE } from "../../lib/people";
import { todayISO } from "../../lib/format";
import type { Filter } from "./DeptFilter";
import type { Me } from "../../auth";

export interface Planning {
  today: string;
  days: PlanDay[] | null;
  loading: boolean;
  error: string | null;
  filter: Filter;
  setFilter: Dispatch<SetStateAction<Filter>>;
  selectedDate: string | null;
  openDay: (date: string) => void;
  closeDay: () => void;
  ack: (date: string, noteId: string) => void;
  addNote: (date: string, body: NoteInput) => void;
  editNote: (date: string, noteId: string, body: NoteInput) => void;
  deleteNote: (date: string, noteId: string) => void;
  myKey: string;
}

export function usePlanning(me: Me, enabled: boolean, flash: (m: string) => void): Planning {
  const today = todayISO();   // the real current date drives "today" / default selection (not the seed anchor)
  const [days, setDays] = useState<PlanDay[] | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>({ all: false, set: [me.dept] });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const myKey = me.email.toLowerCase();

  const reload = useCallback(async () => {
    const r = await getDays();
    setDays(r.days);
  }, []);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try { await reload(); }
      catch (e) { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [enabled, reload]);

  // Optimistic toggle; reconcile from the server on failure.
  const ack = useCallback((date: string, noteId: string) => {
    setDays((ds) => ds && ds.map((d) => d.date !== date ? d : {
      ...d,
      notes: d.notes.map((n) => n.id !== noteId ? n : {
        ...n, ack: n.ack.includes(myKey) ? n.ack.filter((x) => x !== myKey) : [...n.ack, myKey],
      }),
    }));
    ackNote(noteId).catch(() => { flash("Couldn't save acknowledgement"); reload().catch(() => undefined); });
  }, [myKey, flash, reload]);

  const addNote = useCallback((date: string, body: NoteInput) => {
    postNote(date, body).then((r) => {
      setDays((ds) => ds && ds.map((d) => d.date !== date ? d : { ...d, notes: [r.note, ...d.notes] }));
      if (body.mentions.length) {
        const names = body.mentions.map((id) => PEOPLE[id]?.name.split(" ")[0]).filter(Boolean);
        flash(`SMS & push sent to ${names.join(", ")}`);
      } else if (body.pr === "urgent") {
        flash("Urgent — alerted to Big Plan users");
      } else {
        flash("Note posted");
      }
    }).catch((e) => flash(e instanceof Error ? e.message : "Couldn't post note"));
  }, [flash]);

  const editNote = useCallback((date: string, noteId: string, body: NoteInput) => {
    setDays((ds) => ds && ds.map((d) => d.date !== date ? d : {
      ...d, notes: d.notes.map((n) => n.id !== noteId ? n : { ...n, dept: body.dept, pr: body.pr, text: body.text, mentions: body.mentions }),
    }));
    apiEditNote(noteId, body).then(() => flash("Note updated")).catch((e) => { flash(e instanceof Error ? e.message : "Couldn't update note"); reload().catch(() => undefined); });
  }, [flash, reload]);

  const deleteNote = useCallback((date: string, noteId: string) => {
    setDays((ds) => ds && ds.map((d) => d.date !== date ? d : { ...d, notes: d.notes.filter((n) => n.id !== noteId) }));
    apiDeleteNote(noteId).then(() => flash("Note deleted")).catch((e) => { flash(e instanceof Error ? e.message : "Couldn't delete note"); reload().catch(() => undefined); });
  }, [flash, reload]);

  return {
    today, days, loading, error, filter, setFilter, selectedDate,
    openDay: setSelectedDate, closeDay: () => setSelectedDate(null), ack, addNote, editNote, deleteNote, myKey,
  };
}
